import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { CommandLogElasticsearchHit } from '../types/command';

export interface CommandLogFilters {
  textSearch: string;
  userSearch: string[];
  serverIdSearch: string[];
  clusterSearch: string[];
  fqdnSearch: string[];
  dateRange: {
    from: Date;
    to: Date;
  };
  /** Exact keyword match on `playerPos.dimension`; empty disables the filter. */
  playerPosDimension: string;
  /** Coordinate bounds; empty strings mean no bound on that side. */
  playerPosXMin: string;
  playerPosXMax: string;
  playerPosYMin: string;
  playerPosYMax: string;
  playerPosZMin: string;
  playerPosZMax: string;
}

/** The command a context tab is centred on (Discord-style "jump to message"). */
export interface CommandLogContextAnchor {
  hitId: string;
  /** Raw Elasticsearch `ts_ingested` of the anchor command. */
  tsIngested: string;
  fqdn: string;
  fromPlayerName: string;
}

export interface CommandLogContextState {
  anchor: CommandLogContextAnchor;
  /** The anchor command and older ones, newest-first (as returned by ES). */
  beforeHits: CommandLogElasticsearchHit[];
  /** Commands newer than the anchor, oldest-first. */
  afterHits: CommandLogElasticsearchHit[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  initialLoaded: boolean;
}

export interface CommandLogTabState {
  id: string;
  name: string;
  filters: CommandLogFilters;
  hits: CommandLogElasticsearchHit[];
  totalHits: number;
  aggregations: {
    clusters: string[];
  };
  highlightedUsers: { [username: string]: string }; // username -> color
  isLoading: boolean;
  /** When set, the tab shows commands around an anchor instead of filtered search results. */
  context?: CommandLogContextState;
}

interface CommandLogTabsContextType {
  tabs: CommandLogTabState[];
  activeTabId: string | null;
  addTab: (filters?: Partial<CommandLogFilters>, name?: string) => void;
  addContextTab: (anchor: CommandLogContextAnchor, name?: string) => void;
  removeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateFilters: (tabId: string, newFilters: Partial<CommandLogFilters>) => void;
  updateTabState: (tabId: string, newState: Partial<Omit<CommandLogTabState, 'id' | 'filters'>>) => void;
  getActiveTab: () => CommandLogTabState | undefined;
  updateTabName: (id: string, name: string) => void;
  toggleUserHighlight: (tabId: string, username: string) => void;
}

const CommandLogTabsContext = createContext<CommandLogTabsContextType | undefined>(undefined);

export const useCommandLogTabs = () => {
  const context = useContext(CommandLogTabsContext);
  if (!context) {
    throw new Error('useCommandLogTabs must be used within a CommandLogTabsProvider');
  }
  return context;
};

const getDefaultFilters = (): CommandLogFilters => ({
  textSearch: '',
  userSearch: [],
  serverIdSearch: [],
  clusterSearch: [],
  fqdnSearch: [],
  dateRange: {
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  },
  playerPosDimension: '',
  playerPosXMin: '',
  playerPosXMax: '',
  playerPosYMin: '',
  playerPosYMax: '',
  playerPosZMin: '',
  playerPosZMax: '',
});

export const CommandLogTabsProvider = ({ children }: { children: ReactNode }) => {
  const [tabs, setTabs] = useState<CommandLogTabState[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const addTab = useCallback((filters?: Partial<CommandLogFilters>, name = 'New Tab') => {
    const newTab: CommandLogTabState = { 
      id: uuidv4(), 
      name,
      filters: { ...getDefaultFilters(), ...filters },
      hits: [],
      totalHits: 0,
      aggregations: { clusters: [] },
      highlightedUsers: {},
      isLoading: true,
    };
    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabId(newTab.id);
  }, []);

  const addContextTab = useCallback((anchor: CommandLogContextAnchor, name = 'Context') => {
    const newTab: CommandLogTabState = {
      id: uuidv4(),
      name,
      filters: getDefaultFilters(),
      hits: [],
      totalHits: 0,
      aggregations: { clusters: [] },
      highlightedUsers: {},
      isLoading: false,
      context: {
        anchor,
        beforeHits: [],
        afterHits: [],
        hasMoreBefore: false,
        hasMoreAfter: false,
        initialLoaded: false,
      },
    };
    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabId(newTab.id);
  }, []);

  const removeTab = (id: string) => {
    const newTabs = tabs.filter(tab => tab.id !== id);
    setTabs(newTabs);
    if (activeTabId === id) {
      setActiveTabId(newTabs.length > 0 ? newTabs[newTabs.length - 1].id : null);
    }
  };

  const setActiveTab = (id: string) => {
    setActiveTabId(id);
  };

  const updateFilters = useCallback((tabId: string, newFilters: Partial<CommandLogFilters>) => {
    setTabs(prevTabs =>
      prevTabs.map(tab => 
      tab.id === tabId ? { ...tab, filters: { ...tab.filters, ...newFilters } } : tab
    ));
  }, []);
  
  const updateTabState = useCallback((tabId: string, newState: Partial<Omit<CommandLogTabState, 'id' | 'filters'>>) => {
    setTabs(prevTabs =>
      prevTabs.map(tab =>
      tab.id === tabId ? { ...tab, ...newState } : tab
    ));
  }, []);

  const getActiveTab = () => tabs.find(tab => tab.id === activeTabId);

  const updateTabName = (id: string, name: string) => {
    setTabs(tabs.map(tab => tab.id === id ? { ...tab, name } : tab));
  };

  const HIGHLIGHT_COLORS = [
    'rgba(255, 255, 0, 0.1)',   // Yellow
    'rgba(0, 255, 255, 0.1)',   // Cyan
    'rgba(255, 0, 255, 0.1)',   // Magenta
    'rgba(0, 255, 0, 0.1)',     // Green
    'rgba(255, 165, 0, 0.1)',   // Orange
    'rgba(255, 192, 203, 0.1)', // Pink
    'rgba(75, 0, 130, 0.1)',    // Indigo
    'rgba(238, 130, 238, 0.1)', // Violet
    'rgba(255, 0, 0, 0.1)',     // Red
    'rgba(0, 0, 255, 0.1)',     // Blue
  ];

  const toggleUserHighlight = (tabId: string, username: string) => {
    setTabs(tabs.map(tab => {
      if (tab.id === tabId) {
        const newHighlights = { ...tab.highlightedUsers };
        if (newHighlights[username]) {
          delete newHighlights[username];
        } else {
          const usedColors = Object.values(newHighlights);
          const nextColor = HIGHLIGHT_COLORS.find(c => !usedColors.includes(c)) || HIGHLIGHT_COLORS[0];
          newHighlights[username] = nextColor;
        }
        return { ...tab, highlightedUsers: newHighlights };
      }
      return tab;
    }));
  };

  const value = {
    tabs,
    activeTabId,
    addTab,
    addContextTab,
    removeTab,
    setActiveTab,
    updateFilters,
    updateTabState,
    getActiveTab,
    updateTabName,
    toggleUserHighlight,
  };

  return (
    <CommandLogTabsContext.Provider value={value}>
      {children}
    </CommandLogTabsContext.Provider>
  );
};
